/**
 * Hook: Monero node health status — polls a public daemon RPC endpoint
 * to determine if it's online, connecting, or offline.
 */

import { useState, useEffect, useRef } from 'react';
import { getNodeHeight, getNodeUrl, DEFAULT_NODES, type MoneroNode } from '@/lib/monero/nodes';

export type NodeStatus = 'online' | 'connecting' | 'offline';

export function useNodeStatus(nodeUri: string | null) {
  // When no URI is provided (wallet page), default to the first preset node
  const effectiveUri = nodeUri ?? getNodeUrl(DEFAULT_NODES[0]);
  const [status, setStatus] = useState<NodeStatus>('connecting');
  const [height, setHeight] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval>>();

  useEffect(() => {
    const check = async () => {
      setStatus('connecting');
      try {
        // Parse URI into MoneroNode format (pure fetch — no WASM needed)
        const url = new URL(effectiveUri);
        const node: MoneroNode = {
          name: url.hostname,
          host: url.hostname,
          port: parseInt(url.port, 10) || 18081,
          useTls: url.protocol === 'https:',
        };
        const h = await getNodeHeight(node);
        if (h > 0) {
          setStatus('online');
          setHeight(h);
        } else {
          setStatus('offline');
        }
      } catch {
        setStatus('offline');
      }
    };

    check();
    intervalRef.current = setInterval(check, 60000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [effectiveUri]);

  return { status, height };
}
